import React, { useState } from 'react';

import { List, Datagrid, TextField, Edit, Create, SimpleForm, TextInput, BooleanInput, NumberInput, BooleanField, NumberField, DeleteButton, Toolbar, SaveButton, FunctionField, required } from 'react-admin';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useFormState } from 'react-final-form';

import { useTranslate, languageMap, defaultLanguage } from '../common/language';
import { validateMaxLength } from './../components/Validator';

const languageIds = Object.keys(languageMap).map(lang => parseInt(lang, 10)).sort((a, b) => a - b);

const getLanguageIndex = langId => languageIds.indexOf(langId);

const getInitialTranslations = () => languageIds.map(lang => ({ lang, name: '' }));

const getLanguageNameValidators = (lang, translate) => {
  const validators = [validateMaxLength(255, translate('common.validation.maxLength'))];
  if (lang === defaultLanguage) {
    validators.unshift(required());
  }
  return validators;
};

const LanguageNameInput = ({ lang }) => {
  const translate = useTranslate();
  const index = getLanguageIndex(lang);

  if (index < 0) return null;

  return (
    <>
      <TextInput source={`translations.${index}.lang`} defaultValue={lang} style={{ display: 'none' }} label="" />
      <TextInput
        fullWidth
        source={`translations.${index}.name`}
        label={`${translate('events_categories.name')} (${translate(`common.language_${lang}`)})`}
        validate={getLanguageNameValidators(lang, translate)}
      />
    </>
  );
};

export const EventsCategoriesList = props => {
  const translate = useTranslate();

  return (
    <>
      <Typography variant="h5" style={{ marginTop: 24, marginBottom: 16, color: '#666' }}>
        {translate('events_categories.title')}
      </Typography>
      <List
        {...props}
        filter={{ context: 0 }}
        bulkActionButtons={false}
        perPage={1000}
        pagination={false}
        exporter={false}
        sort={{ field: 'order', order: 'ASC' }}
        title={translate('events_categories.title')}
      >
        <Datagrid rowClick="edit">
          <TextField source="name" label={translate('events_categories.name')} sortable={false} cellStyle={{ width: '100%' }} />
          <BooleanField source="actived" label={translate('events_categories.actived')} sortable={false} />
          <NumberField source="order" label={translate('events_categories.order')} sortable={false} />
          <FunctionField
            label=""
            sortable={false}
            cellStyle={{ textAlign: 'right', width: 120, paddingRight: 24 }}
            headerStyle={{ width: 120 }}
            render={record => (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <DeleteButton record={record} undoable={false} label="" />
              </div>
            )}
          />
        </Datagrid>
      </List>
    </>
  );
};

export const EventsCategoriesEdit = props => {
  return (
    <Edit {...props} mutationMode="pessimistic" title={<ElementTitle />}>
      <SimpleForm toolbar={<FormToolbar />}>
        <CategoryForm />
      </SimpleForm>
    </Edit>
  );
};

export const EventsCategoriesCreate = props => {
  const translate = useTranslate();

  return (
    <Create {...props}>
      <SimpleForm redirect="list" initialValues={{ actived: true, order: 0, context: 0, translations: getInitialTranslations() }}>
        <CategoryForm title={translate('events_categories.create')} />
      </SimpleForm>
    </Create>
  );
};

const CategoryForm = ({ title }) => {
  const translate = useTranslate();
  const [activeLanguageTab, setActiveLanguageTab] = useState(0);
  const { errors, submitFailed, values } = useFormState({
    subscription: { errors: true, submitFailed: true, values: true }
  });

  const hasLanguageError = lang => {
    const index = getLanguageIndex(lang);
    const name = values && values.translations && values.translations[index]
      ? values.translations[index].name
      : '';
    const missingDefaultName = lang === defaultLanguage && submitFailed && (!name || !name.trim());
    const fieldError = errors && errors.translations && errors.translations[index] && errors.translations[index].name;
    return missingDefaultName || !!fieldError;
  };

  return (
    <div style={{ width: '100%', boxSizing: 'border-box', padding: '8px 24px 0 24px' }}>
      <Grid container spacing={3}>
        <TextInput source="context" defaultValue={0} style={{ display: 'none' }} label="" />
        {title && (
          <Grid item xs={12}>
            <Typography variant="h5" style={{ color: '#777' }}>{title}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: '0 16px 16px 16px', backgroundColor: '#fff' }}>
            <Tabs
              value={activeLanguageTab}
              onChange={(event, value) => setActiveLanguageTab(value)}
              indicatorColor="primary"
              textColor="primary"
            >
              {languageIds.map(lang => (
                <Tab
                  key={lang}
                  label={translate(`common.language_${lang}`)}
                  style={hasLanguageError(lang) ? { color: '#d32f2f' } : {}}
                />
              ))}
            </Tabs>
            {languageIds.map((lang, index) => (
              <div key={lang} style={{ display: activeLanguageTab === index ? 'block' : 'none', paddingTop: 16 }}>
                <LanguageNameInput lang={lang} />
              </div>
            ))}
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <BooleanInput fullWidth source="actived" label={translate('events_categories.actived')} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <NumberInput fullWidth source="order" label={translate('events_categories.order')} />
        </Grid>
      </Grid>
    </div>
  );
};

const ElementTitle = ({ record }) => {
  const translate = useTranslate();
  return <span>{translate('common.edit')} {record ? `"${record.name}"` : ''}</span>;
};

const FormToolbar = props => (
  <Toolbar {...props} className="user_form_toolbar">
    <SaveButton />
    <DeleteButton redirect="/events_categories" mutationMode="pessimistic" />
  </Toolbar>
);
